import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyFilterPatch,
  clearFilterParams,
  fromSavedFilter,
  readFilterValues,
  sameFilters,
  toSavedFilter,
} from '@bbpos/ui';
import type {
  FilterDateKeys,
  FilterValues,
  SavedFilterItem,
  SavedFiltersPanelProps,
} from '@bbpos/ui';
import type { SavedFilter, SavedFilterPage } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { storeTimezone } from '@/lib/store-clock';
import { ApiError } from '@/lib/api-client';
import { savedFiltersApi } from './api';

/** Owner decision (T27, Q17): MANAGER and OWNER share with the team; anyone saves a private filter. */
const TEAM_SHARE_ROLES = ['OWNER', 'MANAGER'];

export interface ListFilterBarConfig {
  /** The list — a saved filter belongs to it (admin Bills and the POS share `bills`). */
  page: SavedFilterPage;
  /** The list's filter parameters in the URL — the endpoint's own names. */
  keys: string[];
  date?: FilterDateKeys;
  /** The filter holding the store whose clock the dates are on, e.g. `location_id`. */
  locationKey?: string;
  /** A store chosen outside the filter bar (the header's location switcher). */
  locationId?: string | null;
}

export interface ListFilterBar {
  /** The active filters — the list's query parameters, page excluded. */
  values: Record<string, string>;
  onChange: (patch: FilterValues) => void;
  onReset: () => void;
  /** The zone the list's days are on (T18). */
  timeZone: string;
  /** Props for SavedFiltersMenu. */
  favorites: SavedFiltersPanelProps;
}

function errorText(err: unknown): string | null {
  if (!err) return null;
  return err instanceof ApiError || err instanceof Error
    ? err.message
    : 'Could not save the filter';
}

/**
 * The shared filter bar's state for an admin list page (T27): filters in the
 * URL (back button, refresh and links keep them), favourites on the server,
 * a preset range saved as a period, and the user's default favourite applied
 * when the page opens with no filter of its own.
 */
export function useListFilterBar(config: ListFilterBarConfig): ListFilterBar {
  const { page, keys, date, locationKey, locationId } = config;
  const [params, setParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const queryClient = useQueryClient();
  const keysId = keys.join(',');

  const values = useMemo(() => readFilterValues(params, keys), [params, keysId]);
  const zoneFor = useCallback(
    (v: Record<string, string>) =>
      storeTimezone(profile, locationId ?? (locationKey ? v[locationKey] : null)),
    [profile, locationId, locationKey],
  );
  const timeZone = zoneFor(values);

  const onChange = useCallback(
    // Pushed, not replaced: Back undoes the last filter change.
    (patch: FilterValues) => setParams((prev) => applyFilterPatch(prev, patch)),
    [setParams],
  );
  const onReset = useCallback(
    () => setParams((prev) => clearFilterParams(prev, keys)),
    [setParams, keysId],
  );

  const queryKey = ['saved-filters', profile?.tenant_id ?? 'anonymous', page];
  const saved = useQuery({
    queryKey,
    queryFn: () => savedFiltersApi.list(page),
    staleTime: 60_000,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey });
  const create = useMutation({ mutationFn: savedFiltersApi.create, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: ({ id, ...input }: { id: string; is_shared?: boolean; is_default?: boolean }) =>
      savedFiltersApi.update(id, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: savedFiltersApi.remove, onSuccess: invalidate });

  const filters: SavedFilter[] = useMemo(() => saved.data ?? [], [saved.data]);
  const current = toSavedFilter(values, date, timeZone);
  const active = filters.find((f) => sameFilters(f.filters, current));

  // Only the keys this screen knows: a `bills` favourite saved on the till (or
  // in the admin) may carry filters the other screen does not show.
  const apply = useCallback(
    (f: Pick<SavedFilter, 'filters'>, replace = false) => {
      const opened = fromSavedFilter(f.filters, date, zoneFor(f.filters));
      const known = Object.fromEntries(Object.entries(opened).filter(([k]) => keys.includes(k)));
      setParams((prev) => applyFilterPatch(clearFilterParams(prev, keys), known), { replace });
    },
    [date?.fromKey, date?.toKey, zoneFor, setParams, keysId],
  );

  // The user's default favourite, once, when the page opens without filters
  // of its own — a link that carries filters (e.g. a customer's bills) wins.
  const defaultChecked = useRef(false);
  useEffect(() => {
    if (defaultChecked.current || !saved.data) return;
    defaultChecked.current = true;
    const def = saved.data.find((f) => f.is_default && f.is_mine);
    // Replaced, so Back does not land on the unfiltered list it just left.
    if (def && Object.keys(values).length === 0) apply(def, true);
  }, [saved.data, values, apply]);

  const byId = (item: SavedFilterItem) => filters.find((f) => f.id === item.id);

  return {
    values,
    onChange,
    onReset,
    timeZone,
    favorites: {
      filters,
      activeId: active?.id ?? null,
      canShare: TEAM_SHARE_ROLES.includes(profile?.role ?? ''),
      canSave: Object.keys(current).length > 0,
      saving: create.isPending,
      error: errorText(create.error ?? update.error ?? remove.error),
      onApply: (item) => {
        const f = byId(item);
        if (f) apply(f);
      },
      onSave: ({ name, isShared, isDefault }) =>
        create.mutate({ page, name, filters: current, is_shared: isShared, is_default: isDefault }),
      onDelete: (item) => remove.mutate(item.id),
      onToggleDefault: (item) => update.mutate({ id: item.id, is_default: !item.is_default }),
      onToggleShare: (item) => update.mutate({ id: item.id, is_shared: !item.is_shared }),
    },
  };
}
